import { useMemo } from 'react';
import type { LeagueBundle, PlayersFile } from '../../shared/types';
import { Avatar, Empty, PlayerRow, Section, Stat } from '../components/common';
import { efficiency, fmtPct, fmtPoints, fmtRecord, pickLabel } from '../lib/format';
import {
  buildTeamViews,
  groupRoster,
  isPreseason,
  sortStandings,
  summarizePicks,
  teamIndex,
} from '../lib/league';
import { Link } from '../lib/router';

export default function TeamDetail({
  bundle,
  players,
  rosterId,
}: {
  bundle: LeagueBundle;
  players: PlayersFile | null;
  rosterId: number;
}) {
  const views = useMemo(() => buildTeamViews(bundle), [bundle]);
  const index = useMemo(() => teamIndex(views), [views]);
  const team = index.get(rosterId);

  const rank = useMemo(
    () => sortStandings(views).findIndex((t) => t.roster_id === rosterId) + 1,
    [views, rosterId],
  );

  const groups = useMemo(
    () => (team ? groupRoster(team.team, bundle.league.roster_positions, players) : null),
    [team, bundle.league.roster_positions, players],
  );

  const picks = useMemo(() => summarizePicks(bundle.picks, rosterId), [bundle.picks, rosterId]);

  if (!team || !groups) {
    return (
      <div className="center-state">
        <div>No team with roster id {rosterId}.</div>
        <Link to="/teams" className="retry">
          Back to teams
        </Link>
      </div>
    );
  }

  const preseason = isPreseason(bundle);
  const eff = efficiency(team.record);

  return (
    <>
      <header className="page-head">
        <Link to="/teams" className="faint" style={{ fontSize: 12.5 }}>
          ← All teams
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
          <Avatar id={team.avatar} alt={team.name} large />
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 'clamp(26px, 5vw, 40px)', textTransform: 'uppercase' }}>
              {team.name}
            </h1>
            <p style={{ margin: '4px 0 0' }}>
              @{team.manager}
              {!preseason && ` · ${fmtRecord(team.record)} · ${rank}${rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'} place`}
            </p>
          </div>
        </div>
      </header>

      {!preseason && (
        <div className="grid grid-4">
          <Stat label="Record" value={fmtRecord(team.record)} note={`${rank} of ${views.length}`} />
          <Stat label="Points For" value={fmtPoints(team.record.pointsFor)} />
          <Stat label="Points Against" value={fmtPoints(team.record.pointsAgainst)} />
          <Stat
            label="Lineup Efficiency"
            value={eff == null ? '—' : fmtPct(eff)}
            note={
              eff == null
                ? undefined
                : `${fmtPoints(team.record.potentialPoints - team.record.pointsFor)} left on bench`
            }
          />
        </div>
      )}

      <div className="grid grid-2" style={{ marginTop: 24, alignItems: 'start' }}>
        <div className="card card-pad">
          <div className="roster-group">
            <h4>Starting Lineup</h4>
            {groups.starters.map((s, i) => (
              <PlayerRow key={`${s.slot}-${i}`} slot={s.slot} player={s.player} />
            ))}
          </div>

          <div className="roster-group">
            <h4>Bench ({groups.bench.length})</h4>
            {groups.bench.length === 0 ? (
              <Empty>Bench is empty.</Empty>
            ) : (
              groups.bench.map((p) => <PlayerRow key={p.id} player={p} />)
            )}
          </div>

          {groups.taxi.length > 0 && (
            <div className="roster-group">
              <h4>Taxi Squad ({groups.taxi.length})</h4>
              {groups.taxi.map((p) => (
                <PlayerRow key={p.id} player={p} />
              ))}
            </div>
          )}

          {groups.ir.length > 0 && (
            <div className="roster-group">
              <h4>Injured Reserve ({groups.ir.length})</h4>
              {groups.ir.map((p) => (
                <PlayerRow key={p.id} player={p} />
              ))}
            </div>
          )}
        </div>

        <div className="sticky-side">
          <div className="card card-pad">
            <div className="roster-group">
              <h4>Draft Picks Held ({picks.held.length})</h4>
              {picks.held.length === 0 ? (
                <Empty>No picks held.</Empty>
              ) : (
                picks.held.map((p) => {
                  const from = index.get(p.originalRosterId);
                  const own = p.originalRosterId === rosterId;
                  return (
                    <div className="player-row" key={`${p.season}-${p.round}-${p.originalRosterId}`}>
                      <span className={own ? '' : 'asset-pick'} style={{ fontWeight: 600 }}>
                        {pickLabel(p.season, p.round)}
                      </span>
                      <span className="player-meta">
                        {own ? 'own' : `via ${from?.name ?? `roster ${p.originalRosterId}`}`}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {picks.lost.length > 0 && (
              <div className="roster-group">
                <h4>Picks Traded Away ({picks.lost.length})</h4>
                {picks.lost.map((p) => {
                  const to = index.get(p.ownerRosterId);
                  return (
                    <div className="player-row" key={`${p.season}-${p.round}-lost`}>
                      <span className="bad" style={{ textDecoration: 'line-through' }}>
                        {pickLabel(p.season, p.round)}
                      </span>
                      <span className="player-meta">to {to?.name ?? p.ownerRosterId}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Section title="Roster Notes">
            <div className="card card-pad faint" style={{ fontSize: 13 }}>
              {team.team.players.length} players rostered · $
              {bundle.league.waiver_budget - team.record.waiverBudgetUsed} of $
              {bundle.league.waiver_budget} FAAB remaining
              {team.record.waiverPosition ? ` · waiver order #${team.record.waiverPosition}` : ''}
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}
